import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { UserCheck, TrendingUp, Plus, RefreshCw, Download } from "lucide-react";

/**
 * Phase 73 · R1 — Hires Ledger
 *
 * All hires (auto-created when application → Hired stage, plus manual +
 * backfill entries) in one filterable table. Row shows: candidate, job,
 * hired_by, hired_at, TTH, source, status, revenue, cost.
 */
export default function HiresLedger() {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState({ from: "", to: "", status: "", channel: "" });
    const [creating, setCreating] = useState(false);
    const [channels, setChannels] = useState([]);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const params = {};
            if (filter.from) params.period_from = filter.from;
            if (filter.to) params.period_to = filter.to;
            if (filter.status) params.status = filter.status;
            if (filter.channel) params.source_channel = filter.channel;
            const { data } = await api.get("/hires", { params });
            setRows(data?.items || []);
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setLoading(false); }
    }, [filter]);

    useEffect(() => { load(); }, [load]);
    useEffect(() => {
        api.get("/company/source-channels").then(({ data }) => setChannels(data?.channels || [])).catch(() => {});
    }, []);

    const exportCsv = () => {
        const header = ["Candidate", "Job", "Hired By", "Hired At", "TTH (days)", "Source", "Status", "Revenue", "Currency"];
        const lines = [header.join(",")].concat(rows.map((r) => [
            r.candidate?.name || "", r.job?.title || "", r.hired_by_name || "",
            (r.hired_at || "").slice(0, 10),
            r.time_to_hire_days ?? "", r.source_channel || r.source || "",
            r.status || "", r.revenue_resolved ?? "", r.revenue_currency || "USD",
        ].map((v) => `"${String(v).replace(/"/g, '""')}"`).join(",")));
        const blob = new Blob([lines.join("\n")], { type: "text/csv" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url; a.download = `hires-${new Date().toISOString().slice(0, 10)}.csv`; a.click();
        URL.revokeObjectURL(url);
    };

    return (
        <div className="max-w-7xl mx-auto p-3 sm:p-6 lg:p-8" data-testid="hires-page">
            <div className="mb-5 flex items-start justify-between gap-3 flex-wrap">
                <div>
                    <div className="text-xs uppercase tracking-widest font-mono text-ikb">Reporting · Hires</div>
                    <h1 className="font-display font-black text-2xl sm:text-4xl mt-1">Hires ledger</h1>
                    <p className="text-muted-foreground text-sm mt-2 max-w-2xl">Every placement your team has closed. Auto-recorded when an application moves to <b>Hired</b>; also editable for backfills.</p>
                </div>
                <div className="flex items-center gap-2">
                    <Button variant="outline" onClick={exportCsv} className="rounded-none h-9" data-testid="hires-export"><Download className="w-3.5 h-3.5 mr-1.5" /> CSV</Button>
                    <Button onClick={() => setCreating(true)} className="rounded-none h-9 bg-ikb hover:bg-[#001f7a] text-white" data-testid="hires-add"><Plus className="w-3.5 h-3.5 mr-1.5" /> Backfill hire</Button>
                </div>
            </div>

            <div className="border border-border bg-white">
                <div className="flex items-center gap-2 px-3 py-2.5 border-b border-border flex-wrap">
                    <Input type="date" value={filter.from} onChange={(e) => setFilter({ ...filter, from: e.target.value })} className="rounded-none h-8 w-40" data-testid="hires-filter-from" />
                    <span className="text-xs text-muted-foreground">→</span>
                    <Input type="date" value={filter.to} onChange={(e) => setFilter({ ...filter, to: e.target.value })} className="rounded-none h-8 w-40" data-testid="hires-filter-to" />
                    <select value={filter.status} onChange={(e) => setFilter({ ...filter, status: e.target.value })} className="rounded-none h-8 border border-border px-2 text-sm bg-white" data-testid="hires-filter-status">
                        <option value="">All statuses</option>
                        <option value="offered">Offered</option>
                        <option value="accepted">Accepted</option>
                        <option value="joined">Joined</option>
                        <option value="no_show">No-show</option>
                        <option value="rescinded">Rescinded</option>
                    </select>
                    <select value={filter.channel} onChange={(e) => setFilter({ ...filter, channel: e.target.value })} className="rounded-none h-8 border border-border px-2 text-sm bg-white" data-testid="hires-filter-channel">
                        <option value="">All sources</option>
                        {channels.map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                    <Button variant="outline" onClick={load} disabled={loading} className="rounded-none h-8 ml-auto" data-testid="hires-refresh">
                        <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
                    </Button>
                </div>
                {loading ? (
                    <div className="p-8 text-sm text-muted-foreground text-center">Loading…</div>
                ) : rows.length === 0 ? (
                    <div className="p-12 text-center text-sm text-muted-foreground">
                        <UserCheck className="w-8 h-8 mx-auto text-muted-foreground/40" />
                        <div className="mt-3 font-mono uppercase tracking-widest">No hires yet in this window</div>
                    </div>
                ) : (
                    <div className="overflow-auto">
                        <table className="w-full text-sm">
                            <thead className="bg-secondary/40 text-[10px] font-mono uppercase tracking-widest">
                                <tr>
                                    <th className="px-3 py-2 text-left">Candidate</th>
                                    <th className="px-3 py-2 text-left">Job</th>
                                    <th className="px-3 py-2 text-left">Hired by</th>
                                    <th className="px-3 py-2 text-left">Hired</th>
                                    <th className="px-3 py-2 text-right">TTH</th>
                                    <th className="px-3 py-2 text-left">Source</th>
                                    <th className="px-3 py-2 text-left">Status</th>
                                    <th className="px-3 py-2 text-right">Revenue</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-border">
                                {rows.map((r) => (
                                    <tr key={r.id} className="hover:bg-secondary/30" data-testid={`hire-row-${r.id}`}>
                                        <td className="px-3 py-2">
                                            {r.candidate?.name ? (
                                                <Link to={`/candidates/${r.candidate_id}`} className="text-ikb hover:underline">{r.candidate.name}</Link>
                                            ) : <span className="text-muted-foreground">—</span>}
                                        </td>
                                        <td className="px-3 py-2">
                                            {r.job?.title ? (
                                                <Link to={`/jobs/${r.job_id}`} className="hover:underline">{r.job.title}</Link>
                                            ) : <span className="text-muted-foreground">—</span>}
                                        </td>
                                        <td className="px-3 py-2 text-xs">{r.hired_by_name || "—"}</td>
                                        <td className="px-3 py-2 text-xs font-mono">{(r.hired_at || "").slice(0, 10)}</td>
                                        <td className="px-3 py-2 text-right font-mono text-xs">{r.time_to_hire_days ?? "—"}</td>
                                        <td className="px-3 py-2 text-xs">{r.source_channel || r.source || "—"}</td>
                                        <td className="px-3 py-2">
                                            <span className={`text-[10px] font-mono uppercase tracking-widest px-1.5 py-0.5 border ${statusTint(r.status)}`}>{r.status || "—"}</span>
                                        </td>
                                        <td className="px-3 py-2 text-right font-mono text-xs">
                                            {r.revenue_resolved != null ? `${r.revenue_currency || "USD"} ${Number(r.revenue_resolved).toLocaleString()}` : <span className="text-muted-foreground/60">—</span>}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {creating && <CreateHireDialog onClose={() => setCreating(false)} onDone={() => { setCreating(false); load(); }} channels={channels} />}
        </div>
    );
}

function statusTint(s) {
    switch ((s || "").toLowerCase()) {
        case "joined": return "border-emerald-300 bg-emerald-50 text-emerald-700";
        case "accepted": return "border-sky-300 bg-sky-50 text-sky-700";
        case "offered": return "border-amber-300 bg-amber-50 text-amber-700";
        case "no_show": return "border-rose-300 bg-rose-50 text-rose-700";
        case "rescinded": return "border-slate-300 bg-slate-50 text-slate-700";
        default: return "border-border bg-secondary/50 text-muted-foreground";
    }
}

function CreateHireDialog({ onClose, onDone, channels }) {
    const [form, setForm] = useState({
        candidate_id: "", job_id: "", joining_date: "", status: "accepted",
        offer_ctc: "", source_channel: "", revenue_mode: "manual",
        revenue_amount: "", revenue_pct: "", notes: "",
    });
    const [busy, setBusy] = useState(false);

    const submit = async () => {
        if (!form.candidate_id) { toast.error("Candidate ID required"); return; }
        setBusy(true);
        try {
            const payload = { ...form, backfill: true };
            if (payload.offer_ctc) payload.offer_ctc = parseFloat(payload.offer_ctc);
            if (payload.revenue_amount) payload.revenue_amount = parseFloat(payload.revenue_amount);
            if (payload.revenue_pct) payload.revenue_pct = parseFloat(payload.revenue_pct);
            await api.post("/hires", payload);
            toast.success("Hire recorded");
            onDone();
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setBusy(false); }
    };

    return (
        <Dialog open onOpenChange={(v) => !v && onClose()}>
            <DialogContent className="rounded-none max-w-lg" data-testid="hire-create-dialog">
                <DialogHeader>
                    <DialogTitle className="font-display font-bold text-xl">Backfill a hire</DialogTitle>
                    <DialogDescription className="sr-only">Manually record a hire that closed offline or predates automatic tracking.</DialogDescription>
                </DialogHeader>
                <div className="space-y-2.5">
                    <Field label="Candidate ID"><Input value={form.candidate_id} onChange={(e) => setForm({ ...form, candidate_id: e.target.value })} className="rounded-none" data-testid="hire-cid" /></Field>
                    <Field label="Job ID (optional)"><Input value={form.job_id} onChange={(e) => setForm({ ...form, job_id: e.target.value })} className="rounded-none" data-testid="hire-jid" /></Field>
                    <div className="grid grid-cols-2 gap-2">
                        <Field label="Joining date"><Input type="date" value={form.joining_date} onChange={(e) => setForm({ ...form, joining_date: e.target.value })} className="rounded-none" /></Field>
                        <Field label="Status">
                            <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className="w-full h-10 border border-input px-2 text-sm bg-background">
                                <option value="offered">Offered</option>
                                <option value="accepted">Accepted</option>
                                <option value="joined">Joined</option>
                                <option value="no_show">No-show</option>
                                <option value="rescinded">Rescinded</option>
                            </select>
                        </Field>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                        <Field label="Offer CTC"><Input type="number" value={form.offer_ctc} onChange={(e) => setForm({ ...form, offer_ctc: e.target.value })} className="rounded-none" /></Field>
                        <Field label="Source">
                            <select value={form.source_channel} onChange={(e) => setForm({ ...form, source_channel: e.target.value })} className="w-full h-10 border border-input px-2 text-sm bg-background">
                                <option value="">—</option>
                                {channels.map((c) => <option key={c} value={c}>{c}</option>)}
                            </select>
                        </Field>
                    </div>
                    <Field label="Revenue mode">
                        <select value={form.revenue_mode} onChange={(e) => setForm({ ...form, revenue_mode: e.target.value })} className="w-full h-10 border border-input px-2 text-sm bg-background">
                            <option value="manual">Manual amount</option>
                            <option value="percent">% of Offer CTC</option>
                            <option value="contract">From client contract</option>
                        </select>
                    </Field>
                    {form.revenue_mode === "manual" && <Field label="Revenue amount"><Input type="number" value={form.revenue_amount} onChange={(e) => setForm({ ...form, revenue_amount: e.target.value })} className="rounded-none" /></Field>}
                    {form.revenue_mode === "percent" && <Field label="% of Offer CTC"><Input type="number" value={form.revenue_pct} onChange={(e) => setForm({ ...form, revenue_pct: e.target.value })} className="rounded-none" placeholder="8.33" /></Field>}
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose} className="rounded-none">Cancel</Button>
                    <Button onClick={submit} disabled={busy} className="rounded-none bg-ikb hover:bg-[#001f7a] text-white" data-testid="hire-submit">{busy ? "Saving…" : "Save hire"}</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function Field({ label, children }) {
    return (
        <label className="block">
            <span className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">{label}</span>
            <div className="mt-1">{children}</div>
        </label>
    );
}
